import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/features/auth'
import { repositories } from '@/services/repositories'

/** localStorage UI-cache key (contract §1); server settings stay authoritative. */
export const NAV_COLLAPSED_STORAGE_KEY = 'rust-nav-collapsed'

export type NavCollapseState = {
  collapsed: boolean
  setCollapsed: (next: boolean) => void
  toggle: () => void
}

function readLocalCache(): boolean {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function writeLocalCache(value: boolean): void {
  try {
    window.localStorage.setItem(NAV_COLLAPSED_STORAGE_KEY, String(value))
  } catch {
    /* localStorage unavailable — collapse state simply stays in memory */
  }
}

/**
 * Bottom-nav collapse state (contract §1, spec §7).
 * - persists per user via repositories.profile.upsertSettings({ navCollapsed })
 *   when authenticated;
 * - hydrates from server settings on sign-in;
 * - localStorage `rust-nav-collapsed` is only a UI cache / offline fallback.
 */
export function useNavCollapse(): NavCollapseState {
  const { status } = useAuth()
  const [collapsed, setCollapsedState] = useState<boolean>(readLocalCache)

  useEffect(() => {
    if (status !== 'authenticated') return
    let cancelled = false
    repositories.profile
      .getSettings()
      .then((settings) => {
        if (cancelled || !settings) return
        setCollapsedState(settings.navCollapsed)
        writeLocalCache(settings.navCollapsed)
      })
      .catch(() => {
        /* settings unreadable (offline / not configured) — keep local cache */
      })
    return () => {
      cancelled = true
    }
  }, [status])

  const setCollapsed = useCallback(
    (next: boolean) => {
      setCollapsedState(next)
      writeLocalCache(next)
      if (status === 'authenticated') {
        repositories.profile.upsertSettings({ navCollapsed: next }).catch(() => {
          /* local cache keeps the choice until the next successful sync */
        })
      }
    },
    [status],
  )

  const toggle = useCallback(() => {
    setCollapsed(!collapsed)
  }, [collapsed, setCollapsed])

  return { collapsed, setCollapsed, toggle }
}
